import { useCallback, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useActionTimeManagementFilters } from 'infrastructure/hooks/action-time-management/use-filter';
import { ExpandableFilterPanelItem } from 'components/molecules/expandable-filter-panel';
import MonthPicker from 'components/molecules/datepicker/monthPicker';
import DoctorSelectBox from 'components/molecules/doctor-select-box';
import AgencySelectBox from 'components/molecules/agency-select-box';
import ClinicSelectBox from 'components/molecules/clinic-select-box';
import AgencyDoctorSelectBox from 'components/molecules/agency-doctor-select-box';
import ActionTypesSelectBox from 'components/molecules/action-types-select-box';
import BaseSelectBox from 'components/atoms/base-select-box';
import forms from 'constants/forms';
import BaseSearchSelect from 'components/atoms/base-search-select';
import { debounce } from 'lodash';
import { ActionTypes } from 'infrastructure/enums/action-types';
import { useSearchPatient } from 'components/organisms/rpm-ccm-navigation-header/hooks/use-search-patient';
import { ModuleType } from 'infrastructure/enums';
import { ModulesSelectBox } from 'components/molecules/module-select-box';
import { isValidGuid } from 'infrastructure/functions';

import s from './styles.module.scss';

const MAX_LENGTH_FOR_FETCH_DOB = 10;
const excludeFilterTypes = [
  'email',
  'phone',
  'firstName',
  'insuranceNumber',
  'lastName',
];
const options = forms.patientFilterOptions
  .map((opt) => ({
    value: opt.id,
    label: opt.label,
    placeholder: opt.placeholder,
  }))
  .filter(({ value }) => !excludeFilterTypes.includes(value));

const DEFAULT_PATIENT_KEY = options[0];

const Body: FC = () => {
  const { t } = useTranslation();
  const { filter, updateFilter } = useActionTimeManagementFilters();
  const {
    search,
    isLoading: searchPatientsLoading,
    dataOptions: patients,
  } = useSearchPatient({
    module: ModuleType.RPMCCM,
  });

  const debounceSearchPatients = useCallback(debounce(search, 800), []);

  const patientSearchPlaceholder = useMemo(
    () => options.find((o) => o.value === filter.patientKey)?.placeholder,
    [filter.patientKey],
  );

  const update = (value: Partial<typeof filter>) => {
    updateFilter({ page: undefined, ...value });
  };

  return (
    <div className={s.wrapper}>
      <ExpandableFilterPanelItem borderBottom label={t('labels.timeRange')}>
        <MonthPicker
          value={{ year: filter.billingYear, month: filter.billingMonth }}
          onChange={(value) => {
            if (!value) {
              update({
                billingYear: undefined,
                billingMonth: undefined,
              });
            } else {
              update({
                billingYear: value.year,
                billingMonth: value.month,
              });
            }
          }}
        />
      </ExpandableFilterPanelItem>
      <ExpandableFilterPanelItem label={t('labels.patient')} borderBottom>
        <div className={s['patient-item']}>
          <BaseSelectBox
            options={options}
            defaultValue={DEFAULT_PATIENT_KEY.value}
            className={s.dropdown}
            onChange={(v) => {
              updateFilter({
                patientKey: v,
                patientGuid: undefined,
                patientLabel: undefined,
              });
            }}
            value={filter.patientKey}
          />
          <BaseSearchSelect
            isLoading={searchPatientsLoading}
            className={s.search}
            options={patients}
            placeholder={patientSearchPlaceholder}
            value={filter.patientGuid}
            onChange={(v) => {
              const isSearchByDOB =
                filter.patientKey === forms.patientFilterOptions[3].id;
              const isSearchByGuid =
                filter.patientKey === forms.patientFilterOptions[6].id;
              const isValidDOBSearch =
                isSearchByDOB && v.length === MAX_LENGTH_FOR_FETCH_DOB;
              const isSkipLoading =
                (isSearchByDOB && !isValidDOBSearch) ||
                (isSearchByGuid && !isValidGuid(v));

              if (isSkipLoading) {
                return;
              }

              debounceSearchPatients({
                searchValue: v,
                searchKey: filter.patientKey,
              });
            }}
            onSelect={(v, option) => {
              updateFilter({
                page: undefined,
                patientGuid: v,
                patientLabel: option.label as string,
              });
            }}
            onClear={() => {
              updateFilter({
                page: undefined,
                patientGuid: undefined,
                patientLabel: undefined,
              });
            }}
          />
        </div>
      </ExpandableFilterPanelItem>
      <ExpandableFilterPanelItem borderBottom label={t('labels.clinic')}>
        <ClinicSelectBox
          allowClear
          value={filter.clinicGuid}
          agencyGuid={filter.agencyGuid ? [filter.agencyGuid] : undefined}
          onChange={(_, option) => {
            update({
              clinicGuid: option?.value,
              patientGuid: undefined,
              doctorGuid: undefined,
            });
          }}
        />
      </ExpandableFilterPanelItem>
      <ExpandableFilterPanelItem borderBottom label={t('labels.provider')}>
        <DoctorSelectBox
          allowClear
          value={filter.doctorGuid}
          clinicGuid={filter.clinicGuid}
          agencyGuid={filter.agencyGuid}
          onChange={(_, option) =>
            update({
              doctorGuid: option?.value,
              patientGuid: undefined,
            })
          }
        />
      </ExpandableFilterPanelItem>
      <ExpandableFilterPanelItem label={t('labels.agency')}>
        <AgencySelectBox
          allowClear
          value={filter.agencyGuid}
          onChange={(_, option) =>
            update({
              agencyGuid: option?.value,
              clinicGuid: undefined,
              patientGuid: undefined,
              doctorGuid: undefined,
            })
          }
        />
      </ExpandableFilterPanelItem>
      <ExpandableFilterPanelItem label={t('labels.agencyDoctor')}>
        <AgencyDoctorSelectBox
          allowClear
          value={filter.agencyDoctorGuid}
          onChange={(_, option) =>
            update({
              agencyDoctorGuid: option?.value,
              clinicGuid: undefined,
              patientGuid: undefined,
            })
          }
        />
      </ExpandableFilterPanelItem>
      <ExpandableFilterPanelItem label={t('actionTimesManagement.source')}>
        <ModulesSelectBox
          allowClear
          placeholder="All"
          value={filter.source}
          onChange={(value) => {
            update({
              source: !value ? undefined : value,
              actionTypes: undefined,
            });
          }}
          excludeModules={[ModuleType.ADMIN, ModuleType.APCM]}
        />
      </ExpandableFilterPanelItem>
      <ExpandableFilterPanelItem label={t('actionTimesManagement.actionType')}>
        <ActionTypesSelectBox
          allowClear
          showAll
          mode="multiple"
          placeholder="All"
          value={filter.actionTypes}
          maxTagCount="responsive"
          filterByModule={filter.source}
          excludeTypes={[
            // unused
            ActionTypes.MdReview,
            ActionTypes.BulkReadingCommentWithTimer,
            ActionTypes.PatientPastMedicalHistoryDelete,
            ActionTypes.PatientGoalsUpdate,
            ActionTypes.PatientMessage,
            ActionTypes.PatientMessageResponse,
            ActionTypes.ReadingCall,
            // hiding
            ActionTypes.PatientAllergyLogUpdate,
            ActionTypes.PatientPastMedicalHistoryUpdate,
            ActionTypes.PatientICDDelete,
            ActionTypes.PatientICDUpdate,
            ActionTypes.PatientMedicationsDelete,
            ActionTypes.PatientMedicationsUpdate,
            ActionTypes.CCMPatientCallNotes,
            ActionTypes.CCMPatientNotes,
          ]}
          onChange={(value) => {
            update({
              actionTypes: value.includes(ActionTypes.All) ? undefined : value,
            });
          }}
        />
      </ExpandableFilterPanelItem>
    </div>
  );
};

export default Body;
